import React from 'react';
import { HeadingItem } from '@atlaskit/menu';
import { token } from '@atlaskit/tokens';
import { PLANS_ITEM_TYPE } from '@atlassian/jira-favourite-change-provider/src/model/constants.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { getBeforeEntity } from '@atlassian/jira-navigation-apps-common/src/utils/index.tsx';
import { testIdConcat } from '@atlassian/jira-navigation-apps-common/src/utils/test-id.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { FavoriteButton } from '../../../../common/ui/menu/favourite-button/main.tsx';
import { MenuItem } from '../../../../common/ui/menu/item/index.tsx';
import { usePlanNavItems } from '../../../../controllers/items/index.tsx';
import Empty from './empty/main.tsx';
import messages from './messages.tsx';
import type { ContentProps } from './types.tsx';

// request to include icon in atlaskit
// https://ecosystem.atlassian.net/jira/servicedesk/projects/DS/queues/issue/DS-7743
// https://jdog.jira-dev.com/browse/JPO-18296
const IconPlans = () => (
	<svg height={24} width={24} fill="none" viewBox="0 0 24 24">
		<path
			fill={token('color.icon')}
			d="M10 8a1 1 0 000 2h4a1 1 0 100-2h-4zM10 12a1 1 0 011-1h4a1 1 0 110 2h-4a1 1 0 01-1-1zM10 14a1 1 0 100 2h2a1 1 0 100-2h-2z"
		/>

		<path
			fill={token('color.icon')}
			fillRule="evenodd"
			d="M8 4a2 2 0 012-2h4a2 2 0 012 2h1a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2h1zm6 3a2 2 0 001.732-1H17v12H7V6h1.268A2 2 0 0010 7h4zm0-3h-4v1h4V4z"
			clipRule="evenodd"
		/>
	</svg>
);

const Content = ({ testIdPrefix }: ContentProps) => {
	const { formatMessage } = useIntl();
	const {
		recentItems,
		favoriteItems,
		changeFavorite: onChangeFavorite,
		changedItems,
	} = usePlanNavItems();

	const testId = testIdConcat(testIdPrefix, 'items');

	const hasRecentItems = recentItems && recentItems.length > 0;
	const hasFavoriteItems = favoriteItems && favoriteItems.length > 0;

	if (!hasRecentItems && !hasFavoriteItems) {
		return <Empty testIdPrefix={testIdConcat(testIdPrefix, 'empty')} />;
	}

	return hasRecentItems ? (
		<span data-testId={testId}>
			<HeadingItem testId={testIdConcat(testIdPrefix, 'heading')}>
				{formatMessage(messages.recent)}
			</HeadingItem>

			{recentItems.map((item, index) => {
				const { id, favourite: isFavourite, title } = item;
				const contextItem = changedItems[id.toString()];
				const isPending = contextItem && contextItem.pending === true ? contextItem.pending : false;
				const { id: beforeEntityId, type: beforeEntityType } = getBeforeEntity(favoriteItems);
				const itemTitle = title || formatMessage(messages.untitledPlan);

				return (
					<MenuItem
						analytics={{
							actionSubjectId: 'plansNavigationMenuItem',
							menuItemId: id,
							menuItemType: 'recent',
							position: index,
						}}
						href={item.url}
						key={item.id}
						testId={testIdConcat(testIdPrefix, `item-${index}`)}
						iconBefore={<IconPlans />}
						iconAfter={
							<FavoriteButton
								beforeEntityId={beforeEntityId}
								beforeEntityType={beforeEntityType}
								id={String(id)}
								isFavorite={Boolean(isFavourite)}
								favoriteItemName={itemTitle}
								isPending={isPending}
								menuItemType="recent"
								onChangeFavorite={onChangeFavorite}
								type={PLANS_ITEM_TYPE}
							/>
						}
						aria-label={fg('blu-6205_misc_nav3_a11y_fixes') ? itemTitle : undefined}
					>
						{itemTitle}
					</MenuItem>
				);
			})}
		</span>
	) : null;
};

export default Content;
